/**
 * Utilidad para abrir enlaces externos en PWAs sin crear pantallas en blanco
 * 
 * Problema: window.open() en PWAs causa pantallas en blanco al volver
 * Solución: Detectar modo PWA y usar el método apropiado
 */

/**
 * Detecta si la app está corriendo como PWA instalada
 */
export const isRunningAsPWA = () => {
  // Detectar standalone mode (iOS)
  const isIOSStandalone = window.navigator.standalone === true;
  
  // Detectar standalone mode (Android/Desktop)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  
  // Detectar si viene de PWA
  const isPWASource = window.location.search.includes('source=pwa');
  
  return isIOSStandalone || isStandalone || isPWASource;
};

/**
 * Abre un enlace externo de forma segura en PWAs
 * 
 * @param {string} url - URL a abrir
 * @param {boolean} forceNewTab - Forzar apertura en nueva pestaña (desktop)
 */
export const openExternalLink = (url, forceNewTab = false) => {
  if (!url) return;
  
  const isPWA = isRunningAsPWA();
  
  if (isPWA) {
    // En PWA, usar location.href para evitar pantallas en blanco
    // Esto abre el enlace en el navegador del sistema
    window.location.href = url;
  } else {
    // En navegador normal, abrir en nueva pestaña
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Extrae el track ID de una URL o URI de Spotify
 * Soporta: spotify:track:ID  |  https://open.spotify.com/track/ID  |  .../intl-es/track/ID
 *
 * @param {string} urlOrUri
 * @returns {string|null}
 */
export const extractSpotifyTrackId = (urlOrUri) => {
  if (!urlOrUri) return null;
  const s = String(urlOrUri).trim();
  let m = s.match(/spotify:track:([A-Za-z0-9]+)/);
  if (m) return m[1];
  m = s.match(/open\.spotify\.com\/(?:intl-[a-z]{2}\/)?track\/([A-Za-z0-9]+)/);
  if (m) return m[1];
  return null;
};

/**
 * Abre una canción de Spotify y la REPRODUCE.
 *
 * En iOS/PWA usa la URL WEB (`https://open.spotify.com/track/{id}?go=1`) que
 * iOS resuelve vía Universal Link a la app Spotify. En algunos escenarios este
 * camino tiene mejor probabilidad de forzar playback que el URI nativo
 * `spotify:track:{id}` cuando la app ya tenía otra pista cargada.
 * Se combina con un patrón de "retry oportunístico": si el usuario toca el
 * mismo botón <10s después (por ejemplo porque el 1er intento no arrancó),
 * el 2º intento suele forzar el autoplay porque Spotify ya está en foreground.
 *
 * @param {string} spotifyUrl - URL web de Spotify (open.spotify.com/track/...)
 * @param {string} [spotifyUri] - URI nativa opcional (spotify:track:...)
 * @param {Object} [opts]
 * @param {Function} [opts.onOpen] - Callback tras disparar la apertura (ej. mostrar hint UX)
 */
// Estado en memoria para retry oportunístico entre taps consecutivos al mismo track.
let __lastSpotifyOpen = { trackId: null, ts: 0 };

export const openSpotifyTrack = (spotifyUrl, spotifyUri = null, opts = {}) => {
  const trackId = extractSpotifyTrackId(spotifyUri) || extractSpotifyTrackId(spotifyUrl);

  // Sin ID parseable → comportamiento clásico
  if (!trackId) {
    openExternalLink(spotifyUrl);
    return;
  }

  const isPWA = isRunningAsPWA();
  const isIOS =
    /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

  // `go=1` es el param que Spotify usa en sus propios share links → indica al
  // handler de la app que es una apertura activa (referral). Sin garantía oficial
  // pero es el flag que veo en los share URLs generados por la app Spotify iOS.
  const webUrl = `https://open.spotify.com/track/${trackId}?go=1`;

  // Retry oportunístico: 2º tap al mismo track en <10s → Spotify ya está en
  // foreground, el navegador tiene "user gesture" fresco y suele forzar el play.
  const now = Date.now();
  const isRetryTap =
    __lastSpotifyOpen.trackId === trackId && now - __lastSpotifyOpen.ts < 10000;
  __lastSpotifyOpen = { trackId, ts: now };

  if (isPWA || isIOS) {
    // Primary: Universal Link. iOS lo captura → Spotify se abre en la pista.
    window.location.href = webUrl;
  } else {
    window.open(webUrl, '_blank', 'noopener,noreferrer');
  }

  // Notificar al caller (para mostrar toast/hint) — el flag `isRetryTap`
  // permite al UI ocultar el hint la 2ª vez para no molestar.
  if (typeof opts.onOpen === 'function') {
    try {
      opts.onOpen({ trackId, isRetryTap });
    } catch (e) {
      // silencioso
    }
  }
};

/**
 * Crea un manejador de click para enlaces externos
 * Útil para componentes de React
 * 
 * @param {string} url - URL a abrir
 * @returns {Function} - Handler de evento
 */
export const createExternalLinkHandler = (url) => {
  return (event) => {
    event.preventDefault();
    event.stopPropagation();
    openExternalLink(url);
  };
};

/**
 * Props para enlaces externos en React
 * Uso: <a {...getExternalLinkProps(url)}>Link</a>
 * 
 * @param {string} url - URL a abrir
 * @returns {Object} - Props para el elemento <a>
 */
export const getExternalLinkProps = (url) => {
  const isPWA = isRunningAsPWA();
  
  if (isPWA) {
    // En PWA, usar href directo sin target="_blank"
    return {
      href: url,
      rel: 'noopener noreferrer',
      onClick: (e) => {
        e.preventDefault();
        window.location.href = url;
      }
    };
  } else {
    // En navegador, abrir en nueva pestaña
    return {
      href: url,
      target: '_blank',
      rel: 'noopener noreferrer'
    };
  }
};

export default openExternalLink;
